import {
  GetBuyerOfferAnalysisDocument,
  useCalculateOfferScoreMutation,
  useGetOfferRecsLazyQuery,
  useUpdateBuyerOfferAnalysisMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  BuyerOfferAnalysisFragment,
  CompetitivenessAdjustmentInput,
  ListingFragment,
  OfferRecsPayload,
  UpdateBuyerOfferAnalysisInput,
} from '@client/graphql/__generated__/types';
import * as Sentry from '@sentry/react';
import { debounce } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOfferRecsActions } from '~/common/copilot/actions/useOfferRecsActions';
import {
  AgentContingencyMap,
  AgentKeyTermMap,
  getRecommendedMappedAgentOfferRecs,
  getTitle,
  mapAgentOfferRecs,
  transformAgentOfferRecs,
} from '../utils';

const filterMapValues = (map: AgentKeyTermMap | AgentContingencyMap) => {
  return Object.fromEntries(
    Object.entries(map)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, { value }]) => value !== null && value !== undefined)
      .map(([key, { value, notes }]) => {
        const val = {};
        if (value !== null && value !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          Object.assign(val, { value });
        }

        if (notes !== null && notes !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          Object.assign(val, { notes });
        }

        return [key, val];
      })
  );
};

export const useUpdateBuyerOfferAnalysis = ({
  buyerOfferAnalysis,
  listing,
  isViewOnly,
}: {
  buyerOfferAnalysis?: BuyerOfferAnalysisFragment;
  listing?: ListingFragment;
  isViewOnly?: boolean;
}) => {
  const [isAutofillingKeyTerms, setIsAutofillingKeyTerms] = useState(false);
  const [isChangesSaved, setIsChangesSaved] = useState(true);
  const [isCalculatingOfferScore, setIsCalculatingOfferScore] = useState(false);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [agentNotes, setAgentNotes] = useState<string | undefined>(undefined);
  const [agentContingencies, setAgentContingencies] =
    useState<AgentContingencyMap>({});
  const [agentKeyTerms, setAgentKeyTerms] = useState<AgentKeyTermMap>({});

  const [getOfferRecs, { loading: offerRecsLoading }] =
    useGetOfferRecsLazyQuery({
      variables: {
        input: {
          buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
        },
      },
    });

  const [updateBuyerOfferAnalysis, { loading: isUpdatingBuyerOfferAnalysis }] =
    useUpdateBuyerOfferAnalysisMutation({
      refetchQueries: [
        {
          query: GetBuyerOfferAnalysisDocument,
          variables: { uuid: buyerOfferAnalysis?.id || '' },
        },
      ],
    });

  const originalMappedAgentOfferRecs = useMemo(() => {
    return mapAgentOfferRecs(buyerOfferAnalysis);
  }, [buyerOfferAnalysis]);

  const { onKeyTermsUpdate, onScoreUpdate, onCompetitivenessUpdate } =
    useOfferRecsActions(buyerOfferAnalysis, setAgentKeyTerms);

  const [calculateOfferScore] = useCalculateOfferScoreMutation();

  /**
   * This callback is used to update the indigo offer recs stored on the buyer offer analysis.
   */
  const updateIndigoOfferRecs = useCallback(async () => {
    if (isViewOnly) {
      return;
    }

    setIsChangesSaved(false);
    const { data } = await getOfferRecs();

    const offerRecs = data?.getOfferRecs;

    if (!offerRecs) {
      Sentry.captureMessage('No offer recs data returned', {
        extra: {
          buyerOfferAnalysisId: buyerOfferAnalysis?.id ?? 'unknown',
        },
        level: 'warning',
      });
      setIsChangesSaved(true);

      return;
    }

    await updateBuyerOfferAnalysis({
      variables: {
        input: {
          indigoOfferRecs:
            offerRecs as unknown as UpdateBuyerOfferAnalysisInput['indigoOfferRecs'],
          buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
        },
      },
    });
    setIsChangesSaved(true);
  }, [buyerOfferAnalysis, getOfferRecs, updateBuyerOfferAnalysis, isViewOnly]);

  const updateTitle = useCallback(
    (newTitle?: string) => {
      if (isViewOnly) {
        return;
      }

      if (newTitle !== buyerOfferAnalysis?.title) {
        setIsChangesSaved(false);
        const formattedTitle = getTitle(newTitle, listing?.mlsListing);
        void (async () => {
          await updateBuyerOfferAnalysis({
            variables: {
              input: {
                title: formattedTitle,
                buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
              },
            },
          });
          setTitle(formattedTitle);
          setIsChangesSaved(true);
        })();
      }
    },
    [
      updateBuyerOfferAnalysis,
      buyerOfferAnalysis?.id,
      buyerOfferAnalysis?.title,
      listing,
      isViewOnly,
    ]
  );

  const updateHideOfferScore = useCallback(
    (newHideOfferScore: boolean) => {
      if (isViewOnly) {
        return;
      }

      if (newHideOfferScore !== buyerOfferAnalysis?.hideOfferScore) {
        setIsChangesSaved(false);
        void (async () => {
          await updateBuyerOfferAnalysis({
            variables: {
              input: {
                hideOfferScore: newHideOfferScore,
                buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
              },
            },
          });
          setIsChangesSaved(true);
        })();
      }
    },
    [
      updateBuyerOfferAnalysis,
      buyerOfferAnalysis?.id,
      buyerOfferAnalysis?.hideOfferScore,
      isViewOnly,
    ]
  );

  const updateAgentNotes = useCallback(
    (newNotes?: string) => {
      if (isViewOnly) {
        return;
      }

      if (newNotes !== buyerOfferAnalysis?.agentNotes) {
        setIsChangesSaved(false);
        void (async () => {
          await updateBuyerOfferAnalysis({
            variables: {
              input: {
                agentNotes: newNotes || (null as unknown as undefined),
                buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
              },
            },
          });
          setIsChangesSaved(true);
        })();
      }
    },
    [
      updateBuyerOfferAnalysis,
      buyerOfferAnalysis?.id,
      buyerOfferAnalysis?.agentNotes,
      isViewOnly,
    ]
  );

  const recalculateOfferScore = useCallback(
    async (buyerOfferAnalysisId: string) => {
      if (isViewOnly) {
        return;
      }

      setIsCalculatingOfferScore(true);

      const { data } = await calculateOfferScore({
        variables: {
          buyerOfferAnalysisId,
        },
      });
      onScoreUpdate(data?.calculateOfferScore.offerScore);
      setIsCalculatingOfferScore(false);
    },
    [calculateOfferScore, isViewOnly, onScoreUpdate]
  );

  const updateAgentOfferRecs = useCallback(
    async (
      newAgentContingencies: AgentContingencyMap,
      newAgentKeyTerms: AgentKeyTermMap,
      buyerOfferAnalysisId: string,
      hasValueChanges: boolean
    ) => {
      if (isViewOnly) {
        return;
      }

      setIsChangesSaved(false);
      const agentOfferRecs = transformAgentOfferRecs(
        newAgentContingencies,
        newAgentKeyTerms
      );

      onKeyTermsUpdate({
        keyTerms: newAgentKeyTerms,
        contingencies: newAgentContingencies,
      });

      await updateBuyerOfferAnalysis({
        variables: {
          input: {
            agentOfferRecs: agentOfferRecs
              ? (agentOfferRecs as unknown as UpdateBuyerOfferAnalysisInput['agentOfferRecs'])
              : undefined,
            buyerOfferAnalysisId: buyerOfferAnalysisId,
          },
        },
        refetchQueries: [],
      });

      if (hasValueChanges) {
        await recalculateOfferScore(buyerOfferAnalysisId);
      }

      setIsChangesSaved(true);
    },
    [
      isViewOnly,
      onKeyTermsUpdate,
      updateBuyerOfferAnalysis,
      recalculateOfferScore,
    ]
  );

  const updateCompetitiveness = useCallback(
    async (competitivenessAdjustment: CompetitivenessAdjustmentInput) => {
      const buyerOfferAnalysisId = buyerOfferAnalysis?.id;
      if (isViewOnly || !buyerOfferAnalysisId) {
        return;
      }

      const hasLevelChanged =
        buyerOfferAnalysis &&
        competitivenessAdjustment.adjustedLevel !==
          buyerOfferAnalysis?.agentOfferRecs?.competitivenessAdjustment
            ?.adjustedLevel;

      onCompetitivenessUpdate(competitivenessAdjustment);

      await updateBuyerOfferAnalysis({
        variables: {
          input: {
            agentOfferRecs: {
              competitivenessAdjustment,
            },
            buyerOfferAnalysisId,
          },
        },
      });

      if (hasLevelChanged) {
        void recalculateOfferScore(buyerOfferAnalysisId);
      }

      setIsChangesSaved(true);
    },
    [
      buyerOfferAnalysis,
      isViewOnly,
      onCompetitivenessUpdate,
      recalculateOfferScore,
      updateBuyerOfferAnalysis,
    ]
  );

  const updateAgentOfferRecsDebounce = useMemo(
    () => debounce(updateAgentOfferRecs, 500),
    [updateAgentOfferRecs]
  );

  useEffect(() => {
    if (
      buyerOfferAnalysis?.id &&
      // TODO(mike): don't persist indigoOfferRecs if there are transient errors?
      !buyerOfferAnalysis.indigoOfferRecs &&
      !isViewOnly
    ) {
      void (async () => {
        await updateIndigoOfferRecs();
      })();
    }
  }, [buyerOfferAnalysis, updateIndigoOfferRecs, isViewOnly]);

  useEffect(() => {
    const newTitle = getTitle(buyerOfferAnalysis?.title, listing?.mlsListing);

    setTitle(newTitle);
  }, [buyerOfferAnalysis?.title, listing]);

  useEffect(() => {
    setAgentNotes(buyerOfferAnalysis?.agentNotes);
  }, [buyerOfferAnalysis?.agentNotes]);

  // Initialize agentContingencies and agentKeyTerms from BOA agentOfferRecs
  useEffect(() => {
    const { contingencies, keyTerms } = originalMappedAgentOfferRecs;

    if (contingencies || keyTerms) {
      setAgentContingencies(contingencies);
      setAgentKeyTerms(keyTerms);
    }
  }, [originalMappedAgentOfferRecs]);

  // Trigger debounced autosave when agentContingencies or agentKeyTerms change
  useEffect(() => {
    if (isViewOnly) {
      return;
    }

    const hasSomeInput =
      !isEmpty(agentContingencies) || !isEmpty(agentKeyTerms);

    const { contingencies: originalContingencies, keyTerms: originalKeyTerms } =
      originalMappedAgentOfferRecs;
    const hasSomeChanges =
      !isEqual(
        filterMapValues(agentContingencies),
        filterMapValues(originalContingencies)
      ) ||
      !isEqual(
        filterMapValues(agentKeyTerms),
        filterMapValues(originalKeyTerms)
      );

    if (
      hasSomeInput &&
      hasSomeChanges &&
      buyerOfferAnalysis?.id &&
      !isCalculatingOfferScore
    ) {
      setIsChangesSaved(false);

      const hasValueChanges =
        !isEqual(
          Object.values(agentContingencies).map((v) => v.value),
          Object.values(originalContingencies).map((v) => v.value)
        ) ||
        !isEqual(
          Object.values(agentKeyTerms).map((v) => v.value),
          Object.values(originalKeyTerms).map((v) => v.value)
        );

      void updateAgentOfferRecsDebounce(
        agentContingencies,
        agentKeyTerms,
        buyerOfferAnalysis.id,
        hasValueChanges
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentContingencies, agentKeyTerms, buyerOfferAnalysis?.id, isViewOnly]);

  // Fill in median values for agentContingencies and agentKeyTerms if agentOfferRecs is empty
  useEffect(() => {
    if (isViewOnly || !!buyerOfferAnalysis?.agentOfferRecs) {
      return;
    }

    if (buyerOfferAnalysis?.indigoOfferRecs) {
      const recommendedAgentKeyTerms = getRecommendedMappedAgentOfferRecs(
        buyerOfferAnalysis.indigoOfferRecs as OfferRecsPayload
      );
      setAgentKeyTerms(recommendedAgentKeyTerms);
      setIsAutofillingKeyTerms(true);
      setTimeout(() => {
        setIsAutofillingKeyTerms(false);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerOfferAnalysis?.indigoOfferRecs, isViewOnly]);

  return {
    title,
    updateTitle,
    updateAgentNotes,
    updateHideOfferScore,
    isUpdatingBuyerOfferAnalysis,
    agentNotes,
    setAgentNotes,
    setTitle,
    updateCompetitiveness,
    updateIndigoOfferRecs,
    agentContingencies,
    agentKeyTerms,
    setAgentContingencies,
    setAgentKeyTerms,
    isChangesSaved,
    offerRecsLoading,
    isCalculatingOfferScore,
    isAutofillingKeyTerms,
  };
};
